/**
 * The phone Knowledge Archive's body once there are concepts: the graph, full
 * bleed, or the list - whichever the header's toggle says.
 *
 * On the graph a tap PEEKS rather than opens (see `ConceptPeekCard`): the
 * peeked concept is the graph's selection, lit with its links, and "Open
 * concept" hands it to the page's concept panel. A tap on empty canvas puts
 * the peek away. The list opens the panel straight from a tap, as it always
 * has - a row already says what the concept is, so there is nothing to peek.
 *
 * When the panel moves to another concept (its related-concept links), the
 * peek follows, so closing the panel lands on the map where the user ended
 * up rather than where they started.
 */

import { useEffect, useMemo, useState } from 'react';
import { MindspaceGraph } from '@/components/features/mindspace/graph/MindspaceGraph';
import { ConceptMobileList } from '@/components/features/mindspace/graph/ConceptMobileList';
import { ConceptPeekCard } from '@/components/features/mindspace/graph/ConceptPeekCard';
import type { ArchiveView } from '@/hooks/mindspace/useArchiveView';
import type { DecoratedGraph } from '@/lib/mindspace/decorateGraph';
import type { KnowledgeGraphResponse } from '@/lib/api-client';

/** How many of the most important concepts the resting map names. */
const RESTING_LABELS = 5;

interface ArchivePhoneMapProps {
  view: ArchiveView;
  data: KnowledgeGraphResponse;
  graph: DecoratedGraph;
  /** Cluster id → lesson title; see `MindspaceGraph.subjectLabels`. */
  subjectLabels: ReadonlyMap<string, string>;
  /** The concept open in the page's panel, if any. */
  openConceptId: string | null;
  onOpenConcept: (id: string) => void;
}

export function ArchivePhoneMap({
  view,
  data,
  graph,
  subjectLabels,
  openConceptId,
  onOpenConcept,
}: ArchivePhoneMapProps) {
  const [peekId, setPeekId] = useState<string | null>(null);
  useEffect(() => {
    if (openConceptId) setPeekId(openConceptId);
  }, [openConceptId]);

  const peeked = useMemo(
    () => data.nodes.find(n => n.id === peekId) ?? null,
    [data.nodes, peekId]
  );
  // Counted from the API's links, whose ends are ids: the graph's own copies
  // have theirs swapped for node objects once the simulation runs.
  const links = useMemo(
    () =>
      peekId
        ? data.links.filter(l => l.source === peekId || l.target === peekId)
            .length
        : 0,
    [data.links, peekId]
  );

  const hubs = useMemo(
    () =>
      new Set(
        [...data.nodes]
          .sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0))
          .slice(0, RESTING_LABELS)
          .map(n => n.id)
      ),
    [data.nodes]
  );

  if (view === 'list') {
    return <ConceptMobileList nodes={data.nodes} onNodeClick={onOpenConcept} />;
  }

  return (
    <div className='relative h-full'>
      <MindspaceGraph
        subjectLabels={subjectLabels}
        variant='phone'
        data={graph}
        // A peek of a concept the filters have since removed is no peek.
        selectedConceptId={peeked ? peekId : null}
        onNodeClick={setPeekId}
        onBackgroundClick={() => setPeekId(null)}
        restingLabelIds={hubs}
      />
      {peeked && (
        <ConceptPeekCard
          key={peeked.id}
          concept={peeked}
          links={links}
          onOpen={() => onOpenConcept(peeked.id)}
          onClose={() => setPeekId(null)}
        />
      )}
    </div>
  );
}
